// Project-wide audit-log viewer for the designer live preview.
//
// Lists every record add / edit / delete captured across the project's
// preview data, filterable by object and action, rendered with the shared
// <AuditTimeline>. Mirrors the standalone runtime's admin audit page so the
// author sees the same trail their end-users will get post-deploy.

import { useCallback, useEffect, useState } from 'react'
import { AuditTimeline, type AuditEntry } from '../../AuditTimeline'
import { parseApiError } from '../utils'

/** Filters for the project-wide audit-log query. Mirrors the platform's
 *  `PreviewAuditFilters` (livepreview API) shape exactly. */
export interface AuditLogFilters {
  obj_name?: string
  action?: string
  user_id?: string
  /** Interface the change came through: web / assistant / integration / … */
  client?: string
  date_from?: string
  date_to?: string
  limit?: number
  offset?: number
}

/** Fetches the project-wide audit trail. Injected by the hosting app (the
 *  platform pre-binds its `livePreviewAuditLog`) so the package carries no
 *  HTTP client. Mirrors the platform fetcher's signature/return exactly. */
export type AuditLogFetcher = (
  project: string, filters?: AuditLogFilters,
) => Promise<{ ok?: boolean; entries: AuditEntry[] }>

interface Props {
  project: string
  /** Pre-fill the object filter (the currently-open transaction). */
  defaultObjName?: string
  onClose: () => void
  fetchAuditLog: AuditLogFetcher
}

const ACTIONS = ['', 'add', 'edit', 'delete', 'action']
// Interfaces a change can come through (audit `client` stamp).
const CLIENTS = ['', 'web', 'assistant', 'integration', 'process', 'approval', 'agent']

export default function PreviewAuditModal({ project, defaultObjName, onClose, fetchAuditLog }: Props) {
  const [filters, setFilters] = useState<AuditLogFilters>({ limit: 200 })
  const [entries, setEntries] = useState<AuditEntry[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    setEntries(null)
    setError(null)
    fetchAuditLog(project, filters)
      .then((r) => setEntries(r.entries || []))
      .catch((e) => setError(parseApiError(e).message))
  }, [project, filters, fetchAuditLog])

  useEffect(() => { load() }, [load])

  const set = (patch: Partial<AuditLogFilters>) => setFilters((f) => ({ ...f, ...patch }))
  const ipt: React.CSSProperties = { padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 14, background: 'var(--color-bg)', color: 'var(--color-text)' }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001 }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--color-bg, #fff)', color: 'var(--color-text)', borderRadius: 12, padding: 20, width: 640, maxWidth: '92vw', maxHeight: '84vh', overflowY: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.25)' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 17 }}>Audit log</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 }}>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Object</span>
            <input style={ipt} placeholder={defaultObjName || 'all objects'}
              value={filters.obj_name ?? ''}
              onChange={(e) => set({ obj_name: e.currentTarget.value || undefined })} />
          </label>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Action</span>
            <select style={ipt} value={filters.action ?? ''}
              onChange={(e) => set({ action: e.currentTarget.value || undefined })}>
              {ACTIONS.map((a) => <option key={a} value={a}>{a === 'action' ? 'button runs' : a || 'all'}</option>)}
            </select>
          </label>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Client</span>
            <select style={ipt} value={filters.client ?? ''}
              onChange={(e) => set({ client: e.currentTarget.value || undefined })}>
              {CLIENTS.map((c) => <option key={c} value={c}>{c || 'all'}</option>)}
            </select>
          </label>
          <button className="btn btn-secondary btn-sm" onClick={load}>Apply</button>
        </div>

        {error && <div style={{ color: 'var(--color-error, #dc2626)', fontSize: 14 }}>{error}</div>}
        {!error && entries === null && (
          <div style={{ color: 'var(--color-text-secondary)', fontSize: 14, padding: '12px 0' }}>Loading…</div>
        )}
        {!error && entries !== null && <AuditTimeline entries={entries} showObject />}
      </div>
    </div>
  )
}
